<template>
    <div class="card">
        <div class="card-body">
            <div id="graph-div" style="width: 100%; height: 300px; position: relative;">
                <vue-flow v-model="elements" :default-zoom="dynamicZoom" :min-zoom="0.3" :max-zoom="2"
                    :fit-view-on-init="true" @pane-ready="onReady" @nodeMouseEnter="showTooltip"
                    @nodeMouseLeave="hideTooltip">
                    <template #node-custom="nodeProps">
                        <div class="custom-node" :class="[nodeProps.data.isRoot ? 'root-node' : 'leaf-node']">
                            {{ nodeProps.data.name }}
                        </div>
                    </template>
                    <background pattern-color="#aaa" gap="16" />
                    <controls />
                    <mini-map />
                </vue-flow>
            </div>
        </div>
    </div>
</template>

<script>
import { VueFlow, Background, Controls, MiniMap, useVueFlow } from '@vue-flow/core';
import { computed, onMounted, ref, watch, readonly } from 'vue';
import { DateTimeFormat } from '../utils/commonFunction';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';

export default {
    name: 'VueflowGraph',
    components: {
        VueFlow,
        Background,
        Controls,
        MiniMap
    },
    props: ["data", "name", "hoverData", "kind"],
    setup(props) {
        const { fitView } = useVueFlow();
        const elements = ref([]);
        const nodeDimensions = ref({ width: 200, height: 50 });
        const dynamicZoom = ref(0.8);

        // Calculate optimal zoom based on data
        const calculateZoom = computed(() => {
            const leafNodes = props.data.filter(job => job.jobDetails);
            const totalNodes = leafNodes.length;

            if (totalNodes <= 3) {
                return 1.0;
            } else if (totalNodes <= 6) {
                return 0.8;
            } else {
                return 0.6;
            }
        });

        const onReady = () => {
            setTimeout(() => {
                fitView({ padding: 0.2 });
            }, 20);
        };

        const calculateNodes = computed(() => {
            const nodeArray = [];
            const edgeArray = [];

            const rootLabel = props.name || 'Job Stack';
            const rootId = 'root-node';
            const leafNodes = props.data.filter(job => job.jobDetails);

            const verticalLevelY = 150;
            const gap = 40; // space between nodes

            const calculateTextWidth = (text) => {
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');

                // Must match your .custom-node font styles
                context.font = '500 14px Arial';

                const textWidth = context.measureText(text).width;

                const horizontalPadding = 64; // padding: 12px 16px (left + right)
                const minWidth = 160;
                const maxWidth = 280;

                return Math.min(
                    Math.max(textWidth + horizontalPadding, minWidth),
                    maxWidth
                );
            }

            // Calculate root width
            const rootWidth = calculateTextWidth(rootLabel);

            nodeArray.push({
                id: rootId,
                type: 'custom',
                position: { x: 0, y: 0 },
                style: { width: `${rootWidth}px` },
                data: { name: rootLabel, isRoot: true }
            });

            // Calculate leaf widths
            const leafWidths = leafNodes.map(job =>
                calculateTextWidth(job.jobDetails.name)
            );

            // Calculate total width of row
            const totalLeafWidth =
                leafWidths.reduce((sum, w) => sum + w, 0) +
                (leafWidths.length - 1) * gap;

            // Start X so row is centered
            let currentX = -totalLeafWidth / 2;

            leafNodes.forEach((job, index) => {
                const jobDetail = job.jobDetails;
                const nodeId = `job-${jobDetail.name}-${index}`;
                const nodeWidth = leafWidths[index];

                const x = currentX + nodeWidth / 2;
                const y = verticalLevelY;

                nodeArray.push({
                    id: nodeId,
                    type: 'custom',
                    position: { x, y },
                    data: { ...jobDetail, name: jobDetail.name }
                });

                edgeArray.push({
                    id: `edge-${rootId}-${nodeId}`,
                    source: rootId,
                    target: nodeId,
                    type: 'smoothstep',
                    animated: true,
                    style: { stroke: '#6c757d', strokeWidth: 2 }
                });

                currentX += nodeWidth + gap;
            });

            return [...nodeArray, ...edgeArray];
        });


        watch(calculateZoom, (newZoom) => {
            dynamicZoom.value = newZoom;
        }, { immediate: true });

        onMounted(() => {
            elements.value = calculateNodes.value;
        });

        return {
            elements,
            nodeDimensions,
            onReady,
            dynamicZoom: readonly(dynamicZoom)
        };
    },
    methods: {
        showTooltip(event) {
            const { id } = event.node;
            const mouseEvent = event.event;

            const node = this.elements.find(el => el.id === id && !el.source);
            if (!node) return;

            const tooltip = document.createElement('div');

            let tooltipContent = '';
            const fieldsToShow = this.hoverData?.length ? this.hoverData : Object.keys(node.data);

            fieldsToShow.forEach(field => {
                const value = node.data[field];
                if (value !== undefined && value !== null && field !== 'isRoot') {
                    const displayValue = field.includes('_at') || field.includes('_time') ?
                        DateTimeFormat(value) : value;
                    const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    tooltipContent += `<strong>${fieldLabel}: </strong> ${displayValue}<br>`;
                }
            });

            tooltip.innerHTML = `<div>${tooltipContent}</div>`;
            tooltip.style.left = mouseEvent.pageX + 5 + 'px';
            tooltip.style.top = mouseEvent.pageY + 5 + 'px';

            const existingTooltip = document.querySelector('.vueflow-tooltip');
            if (existingTooltip) existingTooltip.remove();

            tooltip.classList.add('vueflow-tooltip');
            document.body.appendChild(tooltip);
        },

        hideTooltip() {
            const existingTooltip = document.querySelector('.vueflow-tooltip');
            if (existingTooltip) existingTooltip.remove();
        }
    }
};
</script>

<style>
.vue-flow {
    width: 100%;
    height: 100%;
}

.vue-flow__node {
    cursor: pointer;
}

.custom-node {
    padding: 12px 16px;
    border-radius: 6px;
    border: 2px solid #dee2e6;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 160px;
    max-width: 280px;
    word-wrap: break-word;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    font-size: 14px;
    font-weight: 500;
    color: #495057;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.vue-flow__edge path {
    stroke: #6c757d;
    opacity: 0.8;
    stroke-width: 2px;
}

.vueflow-tooltip {
    position: absolute;
    pointer-events: none;
    padding: 8px 12px;
    border-radius: 6px;
    background-color: #6E84A3;
    color: white;
    font-size: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-width: 250px;
}

.root-node {
    background: linear-gradient(to left, #5192e7, #2C7BE5);
    color: white;
    border: none;
    font-weight: 600;
    font-size: 15px;
    letter-spacing: 0.5px;
    box-shadow: 0 6px 18px rgba(78, 115, 223, 0.35);
    transform: scale(1.05);
}

.root-node:hover {
    box-shadow: 0 8px 22px rgba(78, 115, 223, 0.45);
}

.leaf-node {
    font-weight: 500;
    font-size: 14px;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: #EDF2F9 !important;
}

body.dark-mode .custom-node {
    background: #1a1d23;
    border-color: #2e3238;
    color: #e1e3e8;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
body.dark-mode .leaf-node {
    background-color: #22252b !important;
}
body.dark-mode .vue-flow__edge path {
    stroke: #6c7293;
}
body.dark-mode .vueflow-tooltip {
    background-color: #282c34;
    color: #e1e3e8;
}

</style>